import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { supabase } from '../../lib/supabase';
import Icon from '../AppIcon';
import RichiestaFerieModalAdmin from '../RichiestaFerieModalAdmin';

// ── Lista zahtjeva za godišnji odmor, STRIKTNO filtrirana po firmi ─────────
// Ovo je komponenta koja je nedostajala u lancu izolacije: čak i uz ispravne
// RLS politike na bazi, ako frontend upit ne filtrira po id_azienda, korisnik
// bi vidio "sve što RLS propušta" bez dodatnog frontend-level filtera — što
// je u redu za sigurnost (RLS je krajnja odbrana), ali loše za UX ako se
// slučajno pomiješaju redovi iz drugog konteksta. Filter je ovdje eksplicitan
// kao defense-in-depth, ne kao zamjena za RLS.
const RichiesteFerieDipendenti = ({ idAzienda, refreshTrigger }) => {
  const { t } = useTranslation();

  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRichiesta, setSelectedRichiesta] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // all | pending | accepted | rejected
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRichieste = useCallback(async () => {
    if (!idAzienda) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('ferie')
        .select('*')
        .eq('id_azienda', idAzienda)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRichieste(data || []);
    } catch (err) {
      console.error('Errore nel recupero delle richieste ferie:', err.message);
    } finally {
      setLoading(false);
    }
  }, [idAzienda]);

  useEffect(() => { fetchRichieste(); }, [fetchRichieste, refreshTrigger]);

  // ── Realtime: auto-refresh kad se promijeni status/upiše novi zahtjev ─────
  // Filter na nivou kanala (filter: `id_azienda=eq.${idAzienda}`) osigurava
  // da se ne prima nepotreban "noise" sa servera za druge firme — RLS bi to
  // ionako blokirao na read-back-u, ali filtriranje na samom kanalu smanjuje
  // nepotreban trafik i re-render.
  useEffect(() => {
    if (!idAzienda) return;

    const channel = supabase
      .channel(`public:ferie:azienda:${idAzienda}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ferie',
          filter: `id_azienda=eq.${idAzienda}`
        },
        () => {
          fetchRichieste();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [idAzienda, fetchRichieste]);

  const handleUpdated = () => {
    fetchRichieste();
  };

  const formatTipo = (tipo) => {
    if (tipo === 'giorno_singolo') return t('ferie.type_single_day');
    if (tipo === 'piu_giorni') return t('ferie.type_multi_day');
    if (tipo === 'ore') return t('ferie.type_hours');
    return tipo;
  };

  const formatDate = (dt) => {
    if (!dt) return '—';
    const locale = i18n.language === 'bs' ? 'bs-BA' : i18n.language === 'it' ? 'it-IT' : i18n.language === 'de' ? 'de-DE' : 'en-GB';
    return new Date(dt).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const filteredRichieste = useMemo(() => {
    return richieste.filter(r => {
      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'pending' && r.accettato === null) ||
        (filterStatus === 'accepted' && r.accettato === true) ||
        (filterStatus === 'rejected' && r.accettato === false);

      const s = searchTerm.toLowerCase();
      const matchesSearch =
        !s ||
        r.nome?.toLowerCase().includes(s) ||
        r.cognome?.toLowerCase().includes(s) ||
        r.email?.toLowerCase().includes(s);

      return matchesStatus && matchesSearch;
    });
  }, [richieste, filterStatus, searchTerm]);

  const StatusBadge = ({ accettato }) => {
    if (accettato === true) {
      return (
        <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-[9px] font-black uppercase tracking-widest border border-emerald-500/20">
          {t('ferie.status_accepted')}
        </span>
      );
    }
    if (accettato === false) {
      return (
        <span className="px-2.5 py-1 rounded-full bg-destructive/10 text-destructive text-[9px] font-black uppercase tracking-widest border border-destructive/20">
          {t('ferie.status_rejected')}
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full bg-orange-500/10 text-orange-500 text-[9px] font-black uppercase tracking-widest border border-orange-500/20">
        {t('ferie.status_pending')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Icon name="Loader2" size={24} className="animate-spin text-primary/50" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filteri */}
      <div className="bg-card p-4 rounded-2xl border border-border shadow-sm flex flex-col md:flex-row gap-4 items-end">
        <div className="flex-[2] w-full relative">
          <Icon name="Search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
          <input
            type="text"
            placeholder={t('ferie.admin.search_placeholder', { defaultValue: 'Cerca per nome, cognome o email...' })}
            className="w-full pl-11 pr-4 py-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none focus:bg-background transition-all font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="flex-1 w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none font-bold"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="all">{t('common.all', { defaultValue: 'Tutti' })}</option>
          <option value="pending">{t('ferie.status_pending')}</option>
          <option value="accepted">{t('ferie.status_accepted')}</option>
          <option value="rejected">{t('ferie.status_rejected')}</option>
        </select>
      </div>

      {/* Tabela */}
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        {filteredRichieste.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Icon name="CalendarX" size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-xs font-bold uppercase tracking-widest">
              {t('ferie.admin.no_requests_found', { defaultValue: 'Nessuna richiesta trovata.' })}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-[10px] uppercase font-black tracking-widest text-muted-foreground">
                  <th className="p-3.5 pl-4">{t('ferie.admin.col_employee', { defaultValue: 'Dipendente' })}</th>
                  <th className="p-3.5">{t('ferie.type_label')}</th>
                  <th className="p-3.5">{t('ferie.from_label')}</th>
                  <th className="p-3.5">{t('ferie.to_label')}</th>
                  <th className="p-3.5">{t('ferie.days_column_label')}</th>
                  <th className="p-3.5">{t('ferie.admin.col_status', { defaultValue: 'Stato' })}</th>
                  <th className="p-3.5 pr-4 text-right">{t('common.actions', { defaultValue: 'Azioni' })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {filteredRichieste.map(r => (
                  <tr key={r.id} className="hover:bg-muted/20 transition-colors">
                    <td className="p-3.5 pl-4 font-bold text-foreground">
                      <p className="text-xs font-bold">{r.nome} {r.cognome}</p>
                      <p className="text-[10px] text-muted-foreground truncate max-w-[160px]">{r.email}</p>
                    </td>
                    <td className="p-3.5 text-xs font-bold">{formatTipo(r.tipo_richiesta)}</td>
                    <td className="p-3.5 text-xs text-muted-foreground">{formatDate(r.dal)}</td>
                    <td className="p-3.5 text-xs text-muted-foreground">{formatDate(r.al)}</td>
                    <td className="p-3.5 text-xs font-bold">{r.giorni > 0 ? r.giorni : '—'}</td>
                    <td className="p-3.5"><StatusBadge accettato={r.accettato} /></td>
                    <td className="p-3.5 pr-4 text-right">
                      <button
                        onClick={() => setSelectedRichiesta(r)}
                        className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg text-muted-foreground transition-all"
                        title={t('common.edit', { defaultValue: 'Dettagli' })}
                      >
                        <Icon name="Info" size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RichiestaFerieModalAdmin
        isOpen={!!selectedRichiesta}
        richiesta={selectedRichiesta}
        currentAdminAziendaId={idAzienda}
        onClose={() => setSelectedRichiesta(null)}
        onUpdated={handleUpdated}
      />
    </div>
  );
};

export default RichiesteFerieDipendenti;